import { Component, OnInit, OnDestroy, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MediatiqueService, Album } from '../mediatique.service';
import { PermissionService } from '../../../../core/service/permission/permission.service';

@Component({
  selector: 'app-mediatique-list',
  standalone: false,
  templateUrl: './mediatique-list.component.html',
  styleUrls: ['./mediatique-list.component.scss'],
})
export class MediatiqueListComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  albums = signal<Album[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  currentPage = signal(1);
  total = signal(0);
  perPage = signal(8);
  searchQuery = signal('');
  selectedStatus = signal('');

  totalPages = computed(() => Math.ceil(this.total() / this.perPage()));
  hasNextPage = computed(() => this.currentPage() < this.totalPages());
  hasPrevPage = computed(() => this.currentPage() > 1);

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private mediatiqueService: MediatiqueService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadAlbums(1, true);
    });
    this.loadAlbums(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  loadAlbums(page: number = this.currentPage(), filtering = false): void {
    if (filtering) {
      this.isFiltering.set(true);
    } else {
      this.isLoading.set(true);
    }
    this.mediatiqueService.getAlbums(page, 8, this.searchQuery(), this.selectedStatus()).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response.success) {
          this.albums.set(response.data);
          this.total.set(response.total);
          this.perPage.set(response.perPage);
          this.currentPage.set(page);
        }
        this.isLoading.set(false);
        this.isFiltering.set(false);
      },
      error: () => {
        this.albums.set([]);
        this.isLoading.set(false);
        this.isFiltering.set(false);
      },
    });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadAlbums(1, true);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.loadAlbums(page);
  }

  onAddNew(): void {
    this.router.navigate(['/federation/mediatique/new']);
  }

  onAlbumClick(id: number): void {
    this.router.navigate(['/federation/mediatique', id]);
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  videoThumbnail(url: string | null | undefined): string | null {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
    const vim = url.match(/vimeo\.com\/(\d+)/);
    if (vim) return `https://vumbnail.com/${vim[1]}.jpg`;
    return null;
  }
}
